import React, { PureComponent } from 'react';
import { EditorState, Modifier, convertToRaw, convertFromRaw, ContentState } from 'draft-js';
import getFragmentFromSelection from 'draft-js/lib/getFragmentFromSelection';

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

// Default maximum block depth supported by Draft.js CSS.
var DRAFT_DEFAULT_MAX_DEPTH = 4;

// Default depth class prefix from Draft.js CSS.
var DRAFT_DEFAULT_DEPTH_CLASS = "public-DraftStyleDefault-depth";

/**
 * Generates CSS styles for list items, for a given selector pattern.
 * @param {string} selectorPrefix
 * @param {number} minDepth
 * @param {number} maxDepth
 */
var generateListNestingStyles = function generateListNestingStyles(selectorPrefix, minDepth, maxDepth) {
  var styles = "";

  for (var depth = minDepth; depth <= maxDepth; depth++) {
    var _prefix = "" + selectorPrefix + depth;
    var counter = "ol" + depth;
    var margin = 1.5 * (depth + 1);

    styles += "\n." + _prefix + ".public-DraftStyleDefault-listLTR { margin-left: " + margin + "em; }\n." + _prefix + ".public-DraftStyleDefault-listRTL { margin-right: " + margin + "em; }\n." + _prefix + ".public-DraftStyleDefault-orderedListItem::before { content: counter(" + counter + ") '. '; counter-increment: " + counter + "; }\n." + _prefix + ".public-DraftStyleDefault-reset { counter-reset: " + counter + "; }";
  }

  return styles;
};

/**
 * Dynamically generates the right list nesting styles.
 * Pure component - will only re-render when `max` changes (eg. never).
 * @param {number} max
 */

var ListNestingStyles = function (_PureComponent) {
  _inherits(ListNestingStyles, _PureComponent);

  function ListNestingStyles() {
    _classCallCheck(this, ListNestingStyles);

    return _possibleConstructorReturn(this, (ListNestingStyles.__proto__ || Object.getPrototypeOf(ListNestingStyles)).apply(this, arguments));
  }

  _createClass(ListNestingStyles, [{
    key: "render",
    value: function render() {
      var _props = this.props,
          prefix = _props.prefix,
          max = _props.max;

      var min = DRAFT_DEFAULT_MAX_DEPTH + 1;

      return max > DRAFT_DEFAULT_MAX_DEPTH ? React.createElement(
        "style",
        null,
        generateListNestingStyles(prefix, min, max)
      ) : null;
    }
  }]);

  return ListNestingStyles;
}(PureComponent);

ListNestingStyles.defaultProps = {
  prefix: DRAFT_DEFAULT_DEPTH_CLASS
};

/**
 * Add depth classes that Draft.js doesn't provide.
 * See https://github.com/facebook/draft-js/blob/232791a4e92d94a52c869f853f9869367bdabdac/src/component/contents/DraftEditorContents-core.react.js#L58-L62.
 * @param {ContentBlock} block
 */
var blockDepthStyleFn = function blockDepthStyleFn(block) {
  var depth = block.getDepth();
  return depth > DRAFT_DEFAULT_MAX_DEPTH ? "" + DRAFT_DEFAULT_DEPTH_CLASS + depth : "";
};

// Custom attribute to store Draft.js content in the HTML clipboard.
var FRAGMENT_ATTR = "data-draftjs-conductor-fragment";

/**
 * Overrides the default copy/cut behavior, adding the serialised Draft.js content to the clipboard data.
 * See also https://github.com/basecamp/trix/blob/62145978f352b8d971cf009882ba06ca91a16292/src/trix/controllers/input_controller.coffee#L415-L422
 * We serialise the editor content within HTML, not as a separate mime type, because Draft.js only allows access
 * to HTML in its paste event handler.
 */
var draftEditorCopyListener = function draftEditorCopyListener(ref, e) {
  var selection = window.getSelection();
  // Get clipboard content like Draft.js would.
  // https://github.com/facebook/draft-js/blob/37989027063ccc8279bfdc99a813b857549512a6/src/component/handlers/edit/editOnCopy.js#L34
  var fragment = getFragmentFromSelection(ref._latestEditorState);

  // Override the default behavior if there is a selection, and content, and clipboardData is supported (IE11 is out).
  if (selection.rangeCount > 0 && fragment && e.clipboardData) {
    var content = ContentState.createFromBlockArray(fragment.toArray());
    var serialisedContent = JSON.stringify(convertToRaw(content));

    // Create a temporary element to store the selection’s HTML.
    // See also Rangy's implementation: https://github.com/timdown/rangy/blob/1e55169d2e4d1d9458c2a87119addf47a8265276/src/core/domrange.js#L515-L520.
    var fragmentElt = document.createElement("div");
    // Modern browsers only support a single range.
    fragmentElt.appendChild(selection.getRangeAt(0).cloneContents());
    fragmentElt.setAttribute(FRAGMENT_ATTR, serialisedContent);
    // We set the style property to replicate the browser's behavior of inline styles in rich text copy-paste.
    // In Draft.js, this is important for line breaks to be interpreted correctly when pasted into another word processor.
    // See https://github.com/facebook/draft-js/blob/a1f4593d8fa949954053e5d5840d33ce1d1082c6/src/component/base/DraftEditor.react.js#L328.
    fragmentElt.setAttribute("style", "white-space: pre-wrap;");

    e.clipboardData.setData("text/plain", selection.toString());
    e.clipboardData.setData("text/html", fragmentElt.outerHTML);

    e.preventDefault();
  }
};

var registerCopySource = function registerCopySource(ref) {
  var editorElt = ref.editor;
  var onCopy = draftEditorCopyListener.bind(null, ref);

  editorElt.addEventListener("copy", onCopy);
  editorElt.addEventListener("cut", onCopy);

  return {
    unregister: function unregister() {
      editorElt.removeEventListener("copy", onCopy);
      editorElt.removeEventListener("cut", onCopy);
    }
  };
};

/**
 * Handles pastes coming from Draft.js editors set up to serialise
 * their Draft.js content within the HTML.
 * This SHOULD NOT be used for stripPastedStyles editor.
 */
var handleDraftEditorPastedText = function handleDraftEditorPastedText(html, editorState) {
  // Plain-text pastes are better handled by Draft.js.
  // flowlint sketchy-null-string:off
  if (!html) {
    return false;
  }

  var doc = new DOMParser().parseFromString(html, "text/html");
  var fragmentElt = doc.querySelector("[" + FRAGMENT_ATTR + "]");

  // Handle the paste if it comes from draftjs-conductor.
  if (fragmentElt) {
    var fragmentAttr = fragmentElt.getAttribute(FRAGMENT_ATTR);
    var rawContent = void 0;

    try {
      // If JSON parsing fails, leave paste handling to Draft.js.
      // There is no reason for this to happen, unless the clipboard was altered somehow.
      // $FlowFixMe
      rawContent = JSON.parse(fragmentAttr);
    } catch (error) {
      return false;
    }

    var fragment = convertFromRaw(rawContent).getBlockMap();

    var content = Modifier.replaceWithFragment(editorState.getCurrentContent(), editorState.getSelection(), fragment);
    return EditorState.push(editorState, content, "insert-fragment");
  }

  return false;
};

export { ListNestingStyles, DRAFT_DEFAULT_MAX_DEPTH, DRAFT_DEFAULT_DEPTH_CLASS, generateListNestingStyles, blockDepthStyleFn, registerCopySource, handleDraftEditorPastedText };
